import { createFileRoute } from "@tanstack/react-router";
import { CalendarDays, MapPin, Ticket } from "lucide-react";
import { listPublicPartnerEvents } from "@/lib/partner-events.functions";

export const Route = createFileRoute("/venue-events")({
  loader: async () => {
    try {
      const { items } = await listPublicPartnerEvents();
      return { items };
    } catch {
      return { items: [] as Awaited<ReturnType<typeof listPublicPartnerEvents>>["items"] };
    }
  },
  head: ({ loaderData }) => {
    const items = loaderData?.items ?? [];
    const url = "https://whatfinland.com/venue-events";
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Venue Events in Finland",
      itemListElement: items.slice(0, 100).map((e: any, i: number) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Event",
          name: e.title,
          startDate: e.starts_at,
          ...(e.ends_at ? { endDate: e.ends_at } : {}),
          ...(e.location ? { location: { "@type": "Place", name: e.location } } : {}),
          ...(e.image_url ? { image: e.image_url } : {}),
          ...(e.description ? { description: e.description } : {}),
          ...(e.ticket_url ? { offers: { "@type": "Offer", url: e.ticket_url } } : {}),
        },
      })),
    };
    return {
      meta: [
        { title: "Venue Events in Finland — Concerts, Gigs & Nights Out" },
        { name: "description", content: "Upcoming events, gigs and cultural nights posted by Finnish venues on What Finland." },
        { property: "og:title", content: "Venue Events in Finland" },
        { property: "og:description", content: `${items.length} upcoming events from Finnish venues.` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(itemListLd) },
      ],
    };
  },
  errorComponent: () => <div className="p-8 text-center text-slate-500">Couldn't load events.</div>,
  notFoundComponent: () => <div className="p-8 text-center text-slate-500">Not found.</div>,
  component: VenueEventsPage,
});

function VenueEventsPage() {
  const { items } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Venue Events</h1>
          <p className="text-slate-600 mt-2">Upcoming gigs, exhibitions and nights out — straight from Finnish venues.</p>
        </header>

        {items.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-500">
            No approved events right now. Check back soon.
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((e: (typeof items)[number]) => (
            <article key={e.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col">
              {e.image_url && (
                <img src={e.image_url} alt={e.title} className="w-full h-44 object-cover" loading="lazy" />
              )}
              <div className="p-4 flex-1 flex flex-col">
                <h2 className="font-semibold text-slate-900 line-clamp-2">{e.title}</h2>
                <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {new Date(e.starts_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </div>
                {e.location && (
                  <div className="mt-1 text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {e.location}
                  </div>
                )}
                {e.description && (
                  <p className="mt-2 text-sm text-slate-700 line-clamp-3">{e.description}</p>
                )}
                {e.ticket_url && (
                  <a
                    href={e.ticket_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-1 rounded-xl bg-slate-900 text-white text-sm font-medium px-3 py-2 hover:bg-slate-800 w-fit"
                  >
                    <Ticket className="h-4 w-4" /> Get Tickets
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
